import React from 'react'
import {
  AttributeManagementCategoryEnum,
  AttributeManagementTypeEnum,
} from '../../../types/attributeManagement.types'
import { LLMPromptTemplateForm } from './LLMPromptTemplateForm'
import SnowflakeQueryInputForm from './SnowflakeQueryInputForm'
import PipelineForm from './PipelineForm'
import QASForm from './QASForm'
import RegexForm from './RegexForm'
import PublishForm from './PublishForm'
import PublishToSnowflakeForm from './PublishToSnowflakeForm'

export type FormMode = 'view' | 'edit' | 'override'

interface Props {
  type: AttributeManagementTypeEnum
  attributes: any
  mode: FormMode
  version: number
  onChange: (attributes: any) => void
}

export interface FormProps {
  attributes: any
  mode: FormMode
  onChange: (attributes: any) => void
  version?: number
}

export const validateAttributes = (
  category: AttributeManagementCategoryEnum,
  type: AttributeManagementTypeEnum,
  attributes: any
) => {
  if (category === AttributeManagementCategoryEnum.ExtractionModel) {
    if (type === AttributeManagementTypeEnum.LLMPromptTemplate) {
      return (
        attributes.prompt &&
        attributes.model &&
        attributes.costAttributionSourceId &&
        attributes.properties.length > 0 &&
        (!attributes.usePrefix || attributes?.outputPrefix?.length)
      )
    }
    return false
  }
  if (category === AttributeManagementCategoryEnum.Input) {
    if (type === AttributeManagementTypeEnum.SnowflakeQueryInput) {
      return (
        attributes.query?.length && (!attributes.useRanking || attributes.rankingField?.length > 0)
      )
    }
    return false
  }
  if (category === AttributeManagementCategoryEnum.Pipeline) {
    if (type === AttributeManagementTypeEnum.Pipeline) {
      if (attributes.enableSchedule && attributes.cronError) {
        return false
      }
      return attributes.steps.filter((step: any) => step !== null).length > 0
    }
    return false
  }
  if (category === AttributeManagementCategoryEnum.Audit) {
    if (type === AttributeManagementTypeEnum.QAS) {
      // Check basic requirements
      if (!attributes.qasWorkflow || attributes.components.length === 0) {
        return false
      }

      // Check that all components have CAMP fields filled
      for (const component of attributes.components) {
        if (!component.campField || component.campField.trim() === '') {
          return false
        }
      }

      return true
    }
    return false
  }
  if (category === AttributeManagementCategoryEnum.DataManipulation) {
    if (type === AttributeManagementTypeEnum.Regex) {
      if (!attributes.mutations.length) {
        return false
      }
      for (const mutation of attributes.mutations) {
        if (!mutation.inputField || !mutation.outputField || !mutation.rules.length) {
          return false
        }
      }
      return true
    }
    return false
  }
  if (category === AttributeManagementCategoryEnum.Publish) {
    if (type === AttributeManagementTypeEnum.Publish) {
      if (!attributes.productIdField) {
        return false
      }
      if (attributes.useRetailerId && !attributes.retailerIdField) {
        return false
      }
      if (attributes.useLocale && !attributes.localeField) {
        return false
      }
      if (attributes.data.length === 0) {
        return false
      }
      const seenAttributeNames = new Set<string>()
      for (const datum of attributes.data) {
        if (!datum.attributeName || !datum.campField) {
          return false
        }
        if (seenAttributeNames.has(datum.attributeName)) {
          return false
        }
        seenAttributeNames.add(datum.attributeName)
      }
      return true
    }
    if (type === AttributeManagementTypeEnum.PublishToSnowflake) {
      if (!attributes.snowflakeTable) {
        return false
      }
      for (const column of attributes.snowflakeColumns || []) {
        if (!column.isPrimaryKey && column.nonNull && !column.defaultValue && !column.campField) {
          return false
        }
      }
      return true
    }
    return false
  }
  return false
}

const StepAttributeForm: React.FC<Props> = ({ type, attributes, mode, version, onChange }) => {
  switch (type) {
    case AttributeManagementTypeEnum.SnowflakeQueryInput:
      return <SnowflakeQueryInputForm attributes={attributes} mode={mode} onChange={onChange} />
    case AttributeManagementTypeEnum.LLMPromptTemplate:
      return <LLMPromptTemplateForm attributes={attributes} mode={mode} onChange={onChange} />
    case AttributeManagementTypeEnum.Pipeline:
      return <PipelineForm attributes={attributes} mode={mode} onChange={onChange} />
    case AttributeManagementTypeEnum.QAS:
      return <QASForm attributes={attributes} mode={mode} onChange={onChange} version={version} />
    case AttributeManagementTypeEnum.Regex:
      return <RegexForm attributes={attributes} mode={mode} onChange={onChange} version={version} />
    case AttributeManagementTypeEnum.Publish:
      return (
        <PublishForm attributes={attributes} mode={mode} onChange={onChange} version={version} />
      )
    case AttributeManagementTypeEnum.PublishToSnowflake:
      return (
        <PublishToSnowflakeForm
          attributes={attributes}
          mode={mode}
          onChange={onChange}
          version={version}
        />
      )
    default:
      return <UnsupportedType type={type} />
  }
}

const UnsupportedType: React.FC<{ type: string }> = ({ type }) => (
  <div>Unsupported Type: {type}</div>
)

export default StepAttributeForm
